import { useEffect, useState } from 'react';
import { ensureFonts, fontSpec } from './ensure-fonts';
import { primaryFontFamily } from './font-options';
import { findFontEntry } from './font-registry';
import { normalizeFontWeight } from './font-weights';

/** `none` — грузить нечего: системный шрифт или шрифт вне набора, предупреждать не о чем. */
export type FontStatus = 'none' | 'loading' | 'loaded' | 'failed';

const unquote = (family: string) => family.replace(/^["']|["']$/g, '');

/**
 * Есть ли уже загруженное начертание. `document.fonts.check()` для этого не годится: у семейства
 * без единого `@font-face` (не подключённый Inter) «загружать нечего», и он отвечает `true`.
 */
function hasLoadedFace(family: string, weight: number): boolean {
  if (typeof document === 'undefined' || !document.fonts) return false;
  let found = false;
  document.fonts.forEach((face) => {
    if (found || face.status !== 'loaded' || unquote(face.family) !== family) return;
    const [from, to = from] = String(face.weight)
      .split(' ')
      .map((part) => normalizeFontWeight(part));
    found = weight >= from && weight <= to;
  });
  return found;
}

/**
 * Состояние загрузки пары «семейство × вес» для индикатора у выбора шрифта. Это свойство браузера,
 * а не данные приложения, поэтому без Redux: статус — результат `ensureFonts` плюс уже загруженные
 * `FontFace` (`document.fonts`). Пока грузится — `loading`, без индикатора.
 */
export function useFontStatus(value: string | undefined, weight?: string | number): FontStatus {
  const family = primaryFontFamily(value);
  const entry = findFontEntry(family);
  const numericWeight = normalizeFontWeight(weight, family);
  const needsLoad = entry !== undefined && entry.source !== 'system';
  const [status, setStatus] = useState<FontStatus>(needsLoad ? 'loading' : 'none');

  useEffect(() => {
    if (!needsLoad) {
      setStatus('none');
      return;
    }
    if (hasLoadedFace(family, numericWeight)) {
      setStatus('loaded');
      return;
    }
    let cancelled = false;
    setStatus('loading');
    ensureFonts([fontSpec(family, numericWeight)]).then((failed) => {
      if (!cancelled) setStatus(failed.length > 0 ? 'failed' : 'loaded');
    });
    return () => {
      cancelled = true;
    };
  }, [family, needsLoad, numericWeight]);

  return status;
}
